// Annotation cards panel (Unit 20, spec Step 7 + §11.2).
//
// Sections in fixed order: OCR flags → Language → Content → Notes. Each section
// shows only unresolved cards. All resolved cards (accepted / dismissed /
// confirmed / corrected) are lifted into a single collapsible "Resolved" section
// at the bottom. Clicking a resolved row expands it to the full editable card
// and highlights the annotation in the scan.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Annotation, AnnotationType } from "../api/types";
import { useSettings } from "../api/hooks";
import { advanceTour } from "./GuidedTour";
import { useAnnotationStore } from "../state/annotationStore";
import { useTourStore } from "../state/tourStore";

import "./AnnotationCards.css";

interface AnnotationCardsProps {
  visibleTypes: Set<AnnotationType>;
}

// Content is graded per task in the Raster view, not as cards here.
const SECTION_ORDER: AnnotationType[] = ["ocr_flag", "language", "note"];

const SECTION_KEY: Record<AnnotationType, string> = {
  ocr_flag: "annotation_card.section_ocr",
  language: "annotation_card.section_language",
  content: "annotation_card.section_content",
  note: "annotation_card.section_note",
};
const TYPE_BADGE_KEY: Record<AnnotationType, string> = {
  ocr_flag: "annotation_card.badge_ocr",
  language: "annotation_card.badge_language",
  content: "annotation_card.badge_content",
  note: "annotation_card.badge_note",
};

const RESOLVED_STATUSES = new Set(["accepted", "dismissed", "confirmed", "corrected", "invalidated"]);

// Reading order: top-to-bottom, then left-to-right (page → y → x). Within a
// line (y within 1% of page height) ties break by x. Applied per section so
// cards always follow the scan even after optimistic create/edit reordering.
function byPosition(a: Annotation, b: Annotation): number {
  if (a.page_number !== b.page_number) return a.page_number - b.page_number;
  if (Math.abs(a.y_norm - b.y_norm) > 0.01) return a.y_norm - b.y_norm;
  return a.x_norm - b.x_norm;
}

export function AnnotationCards({ visibleTypes }: AnnotationCardsProps) {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const defaultDeduction = settings?.default_point_deduction ?? 1;
  const annotations = useAnnotationStore((s) => s.annotations);
  const selectedId = useAnnotationStore((s) => s.selectedId);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const select = useAnnotationStore((s) => s.select);
  const tourActive = useTourStore((s) => s.active);
  const tourAdvancedRef = useRef(false);

  // Keep a ref so the effect below always sees the latest annotations without
  // adding them to the dep array (avoids re-firing on every SWR revalidation).
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  // When an annotation is selected externally (e.g. clicking a margin
  // annotation in the scan overlay), auto-expand its resolved card so it is
  // immediately visible and scrolled into view.
  useEffect(() => {
    if (selectedId == null) return;
    const ann = annotationsRef.current.find((a) => a.id === selectedId);
    if (ann && RESOLVED_STATUSES.has(ann.status)) {
      setExpandedIds((prev) => {
        if (prev.has(selectedId)) return prev;
        const next = new Set(prev);
        next.add(selectedId);
        return next;
      });
    }
  }, [selectedId]);

  const sections = SECTION_ORDER.map((type) => {
    const list = annotations.filter(
      (a) => a.type === type && a.status !== "invalidated" && visibleTypes.has(type),
    );
    const unresolved = list.filter((a) => !RESOLVED_STATUSES.has(a.status)).sort(byPosition);
    const resolved = list.filter((a) => RESOLVED_STATUSES.has(a.status)).sort(byPosition);
    return { type, unresolved, resolved };
  });

  const firstPendingId = sections.flatMap((s) => s.unresolved)[0]?.id ?? null;

  function onTourAnnotationAction() {
    if (tourActive && !tourAdvancedRef.current) {
      tourAdvancedRef.current = true;
      advanceTour();
    }
  }

  const allResolved = sections.flatMap((s) => s.resolved).sort(byPosition);
  const hasAny = sections.some((s) => s.unresolved.length > 0) || allResolved.length > 0;

  if (!hasAny) {
    return (
      <div className="cards-empty">
        <p>{t("annotation_card.empty_title")}</p>
        <p className="cards-empty__hint">{t("annotation_card.empty_hint")}</p>
      </div>
    );
  }

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        select(id);
      }
      return next;
    });
  }

  return (
    <div className="cards">
      {sections.map(({ type, unresolved }) => {
        if (unresolved.length === 0) return null;
        return (
          <section key={type} className="cards__section">
            <h3 className="cards__heading">
              {t(SECTION_KEY[type])}{" "}
              <span className="cards__count">{unresolved.length}</span>
            </h3>
            {unresolved.map((a) => (
              <AnnotationCard
                key={a.id}
                annotation={a}
                defaultDeduction={defaultDeduction}
                tourFirst={a.id === firstPendingId}
                onTourAction={a.id === firstPendingId ? onTourAnnotationAction : undefined}
              />
            ))}
          </section>
        );
      })}

      {allResolved.length > 0 && (
        <section className="cards__section">
          <h3 className="cards__heading">
            {t("annotation_card.status_resolved")}{" "}
            <span className="cards__count">{allResolved.length}</span>
          </h3>
          {allResolved.map((a) =>
            expandedIds.has(a.id) ? (
              <AnnotationCard
                key={a.id}
                annotation={a}
                defaultDeduction={defaultDeduction}
                onCollapse={() => toggleExpand(a.id)}
              />
            ) : (
              <ResolvedRow
                key={a.id}
                annotation={a}
                onExpand={() => toggleExpand(a.id)}
              />
            ),
          )}
        </section>
      )}
    </div>
  );
}

// ── Compact resolved row ───────────────────────────────────────────────────

function ResolvedRow({
  annotation: a,
  onExpand,
}: {
  annotation: Annotation;
  onExpand: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="resolved-card" onClick={onExpand} role="button" tabIndex={0} data-annotation-id={a.id}>
      <span className={`resolved-status resolved-status--${a.status}`}>
        {statusLabel(a.status, t)}
      </span>
      <span className="resolved-summary">{summarize(a, t)}</span>
      <span className="resolved-type-badge">{t(TYPE_BADGE_KEY[a.type])}</span>
    </div>
  );
}

function statusLabel(status: string, t: TFunction): string {
  switch (status) {
    case "accepted":  return t("annotation_card.status_accepted");
    case "dismissed": return t("annotation_card.status_dismissed");
    case "confirmed": return t("annotation_card.status_confirmed");
    case "corrected": return t("annotation_card.status_corrected");
    default:          return status;
  }
}

function summarize(a: Annotation, t: TFunction): string {
  const orig  = a.original_text  ? `"${a.original_text}"` : null;
  const fixed = a.accepted_text ?? a.suggested_text;
  const pts   = a.point_deduction !== 0 ? ` (−${a.point_deduction} pt)` : "";

  if (a.status === "dismissed") {
    return orig ? `${orig} — ${t("annotation_card.summary_dismissed_suffix")}` : t("annotation_card.status_dismissed");
  }
  if (a.status === "confirmed") {
    return orig ? `${orig} — ${t("annotation_card.summary_confirmed_suffix")}` : t("annotation_card.summary_confirmed_standalone");
  }
  if (fixed && a.original_text && fixed !== a.original_text) {
    return `${orig} → "${fixed}"${pts}`;
  }
  if (a.content_note) {
    return `${a.content_note}${pts}`;
  }
  if (orig) {
    return `${orig}${pts}`;
  }
  return `${t("annotation_card.status_resolved")}${pts}`;
}

// ── Full annotation card ───────────────────────────────────────────────────

function AnnotationCard({
  annotation: a,
  defaultDeduction,
  onCollapse,
  tourFirst,
  onTourAction,
}: {
  annotation: Annotation;
  defaultDeduction: number;
  onCollapse?: () => void;
  tourFirst?: boolean;
  onTourAction?: () => void;
}) {
  const { t } = useTranslation();
  const select = useAnnotationStore((s) => s.select);
  const selectedId = useAnnotationStore((s) => s.selectedId);
  const pending = useAnnotationStore((s) => s.pendingIds.has(a.id));
  const accept = useAnnotationStore((s) => s.accept);
  const dismiss = useAnnotationStore((s) => s.dismiss);
  const correct = useAnnotationStore((s) => s.correct);
  const edit = useAnnotationStore((s) => s.edit);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(
    a.accepted_text ?? a.suggested_text ?? a.original_text ?? "",
  );
  // For pending cards with no deduction yet, use the configured default.
  // OCR flags never deduct points.
  const [deduction, setDeduction] = useState<number>(
    a.type === "ocr_flag" ? 0
      : a.status === "pending" && a.point_deduction === 0 ? defaultDeduction
      : a.point_deduction,
  );

  const isSelected = selectedId === a.id;

  async function saveEdit() {
    if (pending) return;
    if (a.type === "ocr_flag") {
      await correct(a.id, draft);
    } else {
      await edit(a.id, {
        status: "accepted",
        accepted_text: draft,
        point_deduction: deduction,
      });
    }
    setEditing(false);
    onTourAction?.();
  }

  return (
    <article
      className={"card" + (isSelected ? " card--selected" : "")}
      onClick={() => select(a.id)}
      role="button"
      tabIndex={0}
      data-annotation-id={a.id}
      {...(tourFirst ? { "data-tour": "first-annotation" } : {})}
    >
      <header className="card__head">
        <span className="card__type">{labelFor(a, t)}</span>
        <div className="card__head-right">
          <span className={`card__status card__status--${a.status}`}>{t(`annotation_card.status_${a.status}`, { defaultValue: a.status })}</span>
          {onCollapse && (
            <button
              type="button"
              className="card__collapse-btn"
              onClick={(e) => { e.stopPropagation(); onCollapse(); }}
              title={t("annotation_card.collapse")}
            >
              ↑
            </button>
          )}
        </div>
      </header>

      <div className="card__body">
        {a.original_text && (
          <p className="card__original">
            <em>{a.original_text}</em>
            {a.suggested_text && (
              <>
                {" → "}
                <strong>{a.suggested_text}</strong>
              </>
            )}
          </p>
        )}
        {a.content_note && <p className="card__note">{a.content_note}</p>}
        {a.rule_category && (
          <p className="card__meta">{a.rule_category}</p>
        )}
      </div>

      {editing && (
        <div className="card__edit" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void saveEdit(); } }}
            placeholder={t("annotation_card.replacement_placeholder")}
            autoFocus
          />
          <div className="card__edit-row">
            {a.type !== "ocr_flag" && (
              <label>
                {t("annotation_card.points_off")}
                <input
                  type="number"
                  step="0.5"
                  value={deduction}
                  onChange={(e) => setDeduction(Number(e.target.value))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void saveEdit(); } }}
                />
              </label>
            )}
            <button
              type="button"
              className="card__btn card__btn--accent"
              disabled={pending}
              onClick={() => void saveEdit()}
            >
              {t("common.save")}
            </button>
            <button
              type="button"
              className="card__btn card__btn--ghost"
              onClick={() => setEditing(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {!editing && (
        <div className="card__actions" onClick={(e) => e.stopPropagation()}>
          {a.type === "ocr_flag" ? (
            <>
              <button
                className="card__btn card__btn--ghost"
                disabled={pending}
                onClick={() => {
                  const correctionText = a.suggested_text ?? a.original_text;
                  if (correctionText) {
                    void correct(a.id, correctionText);
                  } else {
                    edit(a.id, { status: "confirmed" });
                  }
                  onTourAction?.();
                }}
              >
                {t("annotation_card.action_confirm_as_read")}
              </button>
              <button
                className="card__btn card__btn--accent"
                disabled={pending}
                onClick={() => setEditing(true)}
              >
                {t("annotation_card.action_edit")}
              </button>
            </>
          ) : (
            <>
              <label className="card__deduction">
                −
                <input
                  type="number"
                  step="0.5"
                  value={deduction}
                  onChange={(e) => setDeduction(Number(e.target.value))}
                />
                pt
              </label>
              <button
                className="card__btn card__btn--ghost"
                disabled={pending}
                onClick={() => { dismiss(a.id); onTourAction?.(); }}
              >
                {t("annotation_card.action_dismiss")}
              </button>
              <button
                className="card__btn card__btn--accent"
                disabled={pending}
                onClick={() => {
                  accept(a.id, { deduction, acceptedText: a.suggested_text ?? undefined });
                  onTourAction?.();
                }}
              >
                {t("annotation_card.action_accept")}
              </button>
              <button
                className="card__btn card__btn--ghost"
                disabled={pending}
                onClick={() => setEditing(true)}
              >
                {t("annotation_card.action_edit")}
              </button>
            </>
          )}
        </div>
      )}
    </article>
  );
}

function labelFor(a: Annotation, t: TFunction): string {
  switch (a.type) {
    case "ocr_flag": return t("annotation_card.type_ocr_uncertainty");
    case "language": return a.rule_category ?? t("annotation_card.type_language");
    case "content":  return t("annotation_card.type_content");
    case "note":     return t("annotation_card.type_note");
  }
}
